<template>
    <dx-data-grid
        ref="dataGridRef"
        :allow-column-reordering="true"
        :column-chooser="{ enabled: true, mode: 'select' }"
        :column-min-width="50"
        :columns="columns"
        :data-source="dataSource"
        :filter-panel="{ visible: true }"
        :filter-row="{ visible: true }"
        :header-filter="{ visible: true }"
        :height="50"
        :hover-state-enabled="true"
        :remote-operations="{ filtering: true, paging: true, sorting: true, summary: true }"
        :repaint-changes-only="true"
        :row-alternation-enabled="true"
        :show-borders="true"
        :show-column-lines="true"
        :width="'100%'"
        :word-wrap-enabled="true"
        style="flex-grow: 1"
        value-expr="id"
        @toolbar-preparing="onToolbarPreparing($event)"
        @on-cell-prepared="onCellPrepared($event)"
        @content-ready="onContentReady($event)"
        @cell-dbl-click="onInventoryNumberClick($event)"
        @editorPreparing="onEditorPreparing($event)"
    >
        <dx-paging :enabled="true" :page-size="40" />
        <dx-load-panel :enabled="true" />
        <dx-scrolling mode="virtual" />
        <dx-sorting mode="single" />
        <dx-export :allow-export-selected-data="true" :enabled="true" :formats="['pdf', 'excel']" />
        <dx-editing
            :allow-deleting="true"
            :allow-updating="true"
            :refresh-mode="'full'"
            mode="batch"
        />
        <template #startDate="{ data: rowData }">
            <mped-calculation-period-component
                :default-value="rowData.data.start_date"
                :pos-year="5"
                :show-clear-button="true"
                selection-mode="single"
                @value-changed="onStartDateChanged($event, rowData)"
            />
        </template>
        <template #endDate="{ data: rowData }">
            <mped-calculation-period-component
                :default-value="rowData.data.end_date"
                :show-clear-button="true"
                :pos-year="5"
                selection-mode="single"
                @value-changed="onEndDateChanged($event, rowData)"
            />
        </template>
        <template #chooseMvp="{ data: rowData }">
            <DxDropDownBox
                :drop-down-options="{ width: 700 }"
                v-model:value="rowData.data.department"
                :data-source="lookupData"
                :display-expr="() => showDepartment(rowData)"
                value-expr="mvp_code"
                :show-clear-button="false"
            >
                <template #content>
                    <DxTreeList
                        :data-source="lookupData"
                        :columns="gridColumns"
                        :filter-row="{ visible: true }"
                        :focused-row-enabled="true"
                        key-expr="mvp_code"
                        v-model:focused-row-key="rowData.data.department"
                        @focused-row-changed="(e) => onMvzSelectionChanged(e, rowData)"
                        style="max-height: 400px; overflow-y: auto;"
                    />
                </template>
            </DxDropDownBox>
        </template>
        <template #showMvz="{ data: rowData }">
            <div>
                {{ rowData.data.mvz }}
            </div>
        </template>
        <template #checkbox>
            <dx-check-box
                :value="checkbox"
                text="Показывать архив"
                @value-changed="onCheckboxChanged"
            />
        </template>
    </dx-data-grid>
    <mped-popup-grid-view
        ref="mainAssetChooser"
        :choose-item="true"
        :columns="columnsMainAsset"
        :default-filter="filterValue"
        :fn-get-data="getMainAsset"
        popupTitle="Справочник основных средств"
        @selected-item="selectedMainAsset"
    />
</template>

<script>
export default {
    name: 'mped-amortization-excluding-asset-view',
};
</script>

<script setup>

import { onBeforeMount, ref, computed } from 'vue';
import {
    DxDataGrid,
    DxScrolling,
    DxLoadPanel,
    DxSorting,
    DxExport,
    DxPaging,
    DxEditing,
} from 'devextreme-vue/data-grid';
import { DxTreeList } from 'devextreme-vue/tree-list';
import { DxDropDownBox } from 'devextreme-vue/drop-down-box';
import { DxCheckBox } from 'devextreme-vue';

import {
    columnsAmortizationExcluding, columnsPopupMainAsset, mvzLookupColumns,
} from '@/views/mped/services/mped-amortization-service';
import { addNewRowData, createFilterArray, getAiDataSource } from '@/components/utils/ai-data-source-generator';
import {
    getDistributionLookup,
    getExcluding,
    getMainAsset,
    setExcluding,
} from '@/views/mped/services/mped-amortization-api-service';
import { checkHasEditData, getAiToolbar, undoEditData } from '@/components/utils/ai-toolbar-generator';
import MpedCalculationPeriodComponent
    from '@/views/mped/views/calculation/component/mped-calculation-period-component.vue';
import MpedPopupGridView from '@/views/mped/components/mped-popup-grid-view/mped-popup-grid-view.vue';

const mainAssetChooser = ref();
const currentMainAsset = ref();
const dataGridRef = ref();
const gridColumns = mvzLookupColumns;
const columns = columnsAmortizationExcluding('startDate', 'chooseMvp', 'showMvz', 'endDate');
const columnsMainAsset = columnsPopupMainAsset;
const checkbox = ref(false);
let lookupData = [];
let toolbarButtons = {};
let currentMvpCode = ref();

onBeforeMount(async () => {
    const response = await getDistributionLookup();
    lookupData = response.data;
});

const showDepartment = (rowData) => {
    if (rowData?.data?.department) {
        return `${rowData.data.department}`;
    }
};

const dataSource = getAiDataSource(
    {
        dataFn: getExcluding,
        key: 'id',
        remoteOperation: true,
        customFilterFn: () => {
            if (checkbox.value !== true) {
                return createFilterArray([['end_date', '=', null]]);
            }
            return [];
        },
        insertFn: (values) => {
            return setExcluding({ insertDataArgs: [values] });
        },
        updateFn: (id, values) => {
            return setExcluding({ updateDataArgs: [{ id, ...values }] });
        },
        removeFn: (id) => {
            return setExcluding({ deleteDataArgs: [{ id }] });
        },
    },
);

const filterValue = computed(() => {
    return currentMvpCode.value ? ['mvp_code', '=', currentMvpCode.value] : null;
});

const onToolbarPreparing = (e) => {
    const filters = [{
        location: 'before',
        template: 'checkbox',
    }];
    toolbarButtons = getAiToolbar(e, {
        customs: filters,
        refresh: {
            event: reloadData,
        },
        add: {
            event: addData,
        },
        undo: {
            event: (e) => {
                undoEditData(dataGridRef.value.instance);
            },
        },
        save: {
            event: (e) => {
                dataGridRef.value.instance.saveEditData();
            },
        },
    });
};

const onContentReady = (e) => {
    checkHasEditData(dataGridRef.value.instance, toolbarButtons);
};

const onInventoryNumberClick = (e) => {
    if (!e.column || !e.row) return;

    if (e.column.dataField === 'inventory_number') {
        addMainAsset(e);
    }
};

const reloadData = () => {
    dataSource.reload();
};

const onCellPrepared = (e) => {
    if (e.rowType !== 'data' || e.columnIndex != 0) {
        return;
    }
};

const addMainAsset = (e) => {
    mainAssetChooser.value.Open();
};

const selectedMainAsset = (e, reload = true) => {
    currentMainAsset.value = e;
    addData(e);
    if (reload)
        dataSource.reload();
};

const updateChanges = (changesData, refresh = true) => {
    const instance = dataGridRef.value.instance;
    const changes = instance.option('editing.changes') || [];

    const existingChangeIndex = changes.findIndex(c => c.key === changesData.key);

    if (existingChangeIndex >= 0) {
        changes[existingChangeIndex].data = {
            ...changes[existingChangeIndex].data,
            ...changesData.data,
        };
    } else {
        changes.push(changesData);
    }

    if (refresh) {
        instance.option('editing.changes', changes);
        instance.refresh();
    }
};

const addData = (e) => {
    const grid = dataGridRef.value.instance;
    grid.cancelEditData();
    addNewRowData({
        dxInstance: grid,
        sourceData: {
            main_asset_id: e.id,
            thermal_city: 0,
            thermal_suburb: 0,
            electricity_city: 0,
            electricity_suburb: 0,
            incoming_order: e.internal_order,
            class_main_asset: e.class_main_asset,
            ...e,
        },
        fields: ['department', 'thermal_city', 'thermal_suburb', 'electricity_city', 'electricity_suburb',
            'start_date', 'end_date', 'inventory_number', 'main_asset', 'subnumber', 'name', 'main_asset', 'main_asset_id', 'mvz',
            'incoming_order', 'class_main_asset'],
    });
};

const handleDateChange = async (e, rowData, fieldName) => {
    const newDate = e.pop();

    if (rowData.data.id && newDate !== rowData.data[fieldName]) {
        updateChanges({
            type: 'update',
            key: rowData.data.id,
            data: {
                [fieldName]: newDate ? newDate : null,
            },
        }, false);
    } else {
        rowData.data[fieldName] = newDate;
    }
};

const onStartDateChanged = async (e, rowData) => {
    await handleDateChange(e, rowData, 'start_date');
};

const onEndDateChanged = async (e, rowData) => {
    await handleDateChange(e, rowData, 'end_date');
};

const onMvzSelectionChanged = (e, rowData) => {
    if (!e?.row?.data || !rowData?.data) return;
    const { mvz: newMvz, mvp_code: newMvp, name: newName } = e.row.data;
    rowData.data.mvz = newMvz;
    rowData.data.department = `${newMvp} - ${newName}`;
    currentMvpCode.value = newMvp;


    if (dataGridRef.value.instance && dataGridRef.value.instance.refresh) {
        dataGridRef.value.instance.refresh();
    }

    if (rowData.data.id) {
        updateChanges({
            type: 'update',
            key: rowData.data.id,
            data: {
                mvz: newMvz,
                department: `${newMvp} - ${newName}`,
            },
        }, true);
    }
};

const onEditorPreparing = (e) => {
    if (e.parentType === 'dataRow' && e.dataField === 'mvz') {
        e.editorOptions.maxLength = 10;
    }
}

const onCheckboxChanged = (e) => {
    checkbox.value = e.value;
    reloadData();
};
</script>
